import React, { useState, useEffect } from 'react';
import { BadgeCheck, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useVerifiedArtists } from '../../context/VerifiedArtistContext';
import { useTheme } from '../../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifiedCelebration = () => {
  const { user, refreshUser } = useAuth();
  const { isVerified, refreshVerified } = useVerifiedArtists();
  const { isDarkMode } = useTheme();
  const [show, setShow] = useState(false);
  const [artistName, setArtistName] = useState('');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    
    // Don't show celebration on admin routes
    if (window.location.pathname.startsWith('/admin')) return;

    const checkVerificationNotification = async () => {
      try {
        const token = localStorage.getItem('tapInToken');
        if (!token) return;

        const res = await fetch(`${API_URL}/api/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;

        const notifications = await res.json();
        const verifiedNotif = (notifications || []).find(
          n => n.notification_type === 'artist_verified' && !n.is_read
        );

        if (verifiedNotif) {
          const celebratedKey = `tapIn_celebrated_${user.id}`;
          const alreadyCelebrated = localStorage.getItem(celebratedKey);
          if (!alreadyCelebrated) {
            setArtistName(verifiedNotif.metadata?.artist_name || '');
            await refreshVerified();
            setShow(true);
            setTimeout(() => setAnimateIn(true), 50);
            localStorage.setItem(celebratedKey, 'true');
          }
        }
      } catch (err) {
        // Silent
      }
    };

    checkVerificationNotification();
    const interval = setInterval(checkVerificationNotification, 15000);
    return () => clearInterval(interval);
  }, [user?.id, refreshVerified]);

  const handleClose = async () => {
    setAnimateIn(false);
    // Refresh verified artists list and user data so gold check appears immediately
    await refreshVerified();
    if (refreshUser) await refreshUser();
    setTimeout(() => setShow(false), 300);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${animateIn ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-xs rounded-3xl overflow-hidden transition-all duration-500 ${
          animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8'
        } ${isDarkMode ? '' : 'shadow-xl'}`}
        style={{ background: isDarkMode 
          ? 'linear-gradient(160deg, #0f0f0f 0%, #1a1a2e 50%, #0f0f0f 100%)' 
          : 'linear-gradient(160deg, #ffffff 0%, #fefce8 50%, #ffffff 100%)' 
        }}
        data-testid="verified-celebration-modal"
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
            isDarkMode 
              ? 'bg-white/10 text-white/60 hover:text-white hover:bg-white/20' 
              : 'bg-black/5 text-gray-400 hover:text-gray-600 hover:bg-black/10'
          }`}
          data-testid="close-celebration-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="px-8 pt-12 pb-10 text-center">
          {/* Badge with glow */}
          <div className="relative inline-flex items-center justify-center mb-6">
            <div
              className="absolute w-24 h-24 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(234,179,8,0.3) 0%, transparent 70%)',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
            <div
              className="relative w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
            >
              <BadgeCheck className="w-9 h-9 text-white" strokeWidth={2.5} />
            </div>
          </div>

          {/* Title */}
          <h2 className={`text-2xl font-bold mb-2 tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            You're Verified!
          </h2>

          {/* Artist name */}
          {artistName && (
            <p className="text-amber-500 text-sm font-medium mb-4">
              {artistName}
            </p>
          )}

          {/* Description */}
          <p className={`text-sm leading-relaxed mb-8 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
            Your gold badge is now live across Tap In. Your profile, sessions, and playlists will display your verified status.
          </p>

          {/* CTA */}
          <button
            onClick={handleClose}
            className="w-full py-3.5 rounded-2xl font-semibold text-sm text-black transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)' }}
            data-testid="celebration-continue-btn"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifiedCelebration;
